import React, { useState, useEffect } from 'react';
import { Card, Form, Input, InputNumber, Button, Alert, Space, Typography, Switch, Radio, message, Tag, Divider } from 'antd';
import { SaveOutlined, DollarOutlined, CheckCircleFilled } from '@ant-design/icons';
import { adminAPI } from '../api/admin';

const { Text } = Typography;

const DEFAULT_ZARINPAL = { enabled: true, sandbox: false, hasMerchantId: false, merchantId: '' };
const DEFAULT_ZIBAL = { enabled: false, sandbox: true, hasMerchant: false, merchant: '' };
const DEFAULT_OXAPAY = {
  enabled: false, sandbox: true,
  hasApiKey: false, apiKey: '',
  hasGeneralApiKey: false, generalApiKey: '',
  usdToTomanRate: 0, donationLink: ''
};

const GATEWAY_LABEL = { zarinpal: 'زرین‌پال', zibal: 'زیبال', oxapay: 'OxaPay' };

export default function PaymentSettingsManager() {
  const [zarinpalForm] = Form.useForm();
  const [zibalForm] = Form.useForm();
  const [oxapayForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [activeGateway, setActiveGateway] = useState('zarinpal');
  const [zarinpal, setZarinpal] = useState(DEFAULT_ZARINPAL);
  const [zibal, setZibal] = useState(DEFAULT_ZIBAL);
  const [oxapay, setOxapay] = useState(DEFAULT_OXAPAY);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getPaymentSettings();
      if (res.data.success) {
        const s = res.data.settings;
        setActiveGateway(s.activeGateway || 'zarinpal');
        setZarinpal({ ...DEFAULT_ZARINPAL, ...s.zarinpal });
        setZibal({ ...DEFAULT_ZIBAL, ...s.zibal });
        setOxapay({ ...DEFAULT_OXAPAY, ...s.oxapay });
        zarinpalForm.setFieldsValue({ merchantId: '' });
        zibalForm.setFieldsValue({ merchant: '' });
        oxapayForm.setFieldsValue({
          apiKey: '',
          generalApiKey: '',
          usdToTomanRate: s.oxapay?.usdToTomanRate || undefined,
          donationLink: s.oxapay?.donationLink || ''
        });
      }
    } catch {
      message.error('خطا در بارگذاری تنظیمات پرداخت');
    } finally {
      setLoading(false);
    }
  };

  const buildPayload = (overrides = {}) => ({
    activeGateway,
    zarinpal: {
      enabled: zarinpal.enabled,
      sandbox: zarinpal.sandbox,
      merchantId: zarinpalForm.getFieldValue('merchantId') || undefined
    },
    zibal: {
      enabled: zibal.enabled,
      sandbox: zibal.sandbox,
      merchant: zibalForm.getFieldValue('merchant') || undefined
    },
    oxapay: {
      enabled: oxapay.enabled,
      sandbox: oxapay.sandbox,
      apiKey: oxapayForm.getFieldValue('apiKey') || undefined,
      generalApiKey: oxapayForm.getFieldValue('generalApiKey') || undefined,
      usdToTomanRate: oxapayForm.getFieldValue('usdToTomanRate') || undefined,
      donationLink: oxapayForm.getFieldValue('donationLink') || undefined
    },
    ...overrides
  });

  const saveSettings = async (overrides, successMsg) => {
    setSaving(true);
    try {
      const res = await adminAPI.updatePaymentSettings(buildPayload(overrides));
      if (res.data.success) {
        message.success(successMsg || 'تنظیمات پرداخت با موفقیت ذخیره شد');
        await loadSettings();
      } else {
        message.error(res.data.message || 'خطا در ذخیره');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || 'خطا در اتصال');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = () => saveSettings();

  const handleActiveGatewayChange = (e) => {
    const value = e.target.value;
    setActiveGateway(value);
    saveSettings({ activeGateway: value }, `درگاه فعال روی «${GATEWAY_LABEL[value] || value}» تنظیم شد`);
  };

  const handleToggleGateway = (gatewayKey, checked) => {
    if (gatewayKey === 'zarinpal') setZarinpal((prev) => ({ ...prev, enabled: checked }));
    else if (gatewayKey === 'zibal') setZibal((prev) => ({ ...prev, enabled: checked }));
    else setOxapay((prev) => ({ ...prev, enabled: checked }));

    saveSettings(
      { [gatewayKey]: { enabled: checked } },
      `درگاه ${GATEWAY_LABEL[gatewayKey] || gatewayKey} ${checked ? 'فعال' : 'غیرفعال'} شد`
    );
  };

  const noGatewayEnabled = !zarinpal.enabled && !zibal.enabled && !oxapay.enabled;

  return (
    <div style={{ maxWidth: 720 }}>
      <Alert
        type="info"
        showIcon
        icon={<DollarOutlined />}
        style={{ marginBottom: 20 }}
        message="تنظیمات درگاه‌های پرداخت"
        description="می‌توانید هر یک از درگاه‌های زرین‌پال، زیبال و OxaPay (کریپتو) را جداگانه فعال یا غیرفعال کنید و مشخص کنید کدام درگاه به‌صورت پیش‌فرض برای خریدهای سایت ایرانی (potterhead.ir) استفاده شود. سایت بین‌المللی (potterheads.org) صرف‌نظر از این تنظیمات همیشه فقط OxaPay را می‌بیند."
      />

      {noGatewayEnabled && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 20 }}
          message="هیچ درگاه پرداختی فعال نیست"
          description="با این تنظیمات، کاربران نمی‌توانند پرداخت انجام دهند. حداقل یک درگاه را فعال کنید."
        />
      )}

      <Card
        loading={loading}
        title="درگاه فعال پیش‌فرض"
        style={{ marginBottom: 20 }}
        size="small"
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          برای خریدهایی که کاربر خودش درگاه را انتخاب نمی‌کند (بلیت قطار، بسته کامل ورود، بسته هدیه، بسته گالیون)، این درگاه استفاده می‌شود — فقط روی سایت ایرانی؛ سایت بین‌المللی همیشه OxaPay است.
        </Text>
        <Radio.Group value={activeGateway} onChange={handleActiveGatewayChange} disabled={saving}>
          <Radio.Button value="zarinpal">
            زرین‌پال {zarinpal.enabled ? <CheckCircleFilled style={{ color: '#52c41a', marginRight: 4 }} /> : <Tag color="default" style={{ marginRight: 4 }}>غیرفعال</Tag>}
          </Radio.Button>
          <Radio.Button value="zibal">
            زیبال {zibal.enabled ? <CheckCircleFilled style={{ color: '#52c41a', marginRight: 4 }} /> : <Tag color="default" style={{ marginRight: 4 }}>غیرفعال</Tag>}
          </Radio.Button>
          <Radio.Button value="oxapay">
            OxaPay {oxapay.enabled ? <CheckCircleFilled style={{ color: '#52c41a', marginRight: 4 }} /> : <Tag color="default" style={{ marginRight: 4 }}>غیرفعال</Tag>}
          </Radio.Button>
        </Radio.Group>
      </Card>

      {/* ─── زرین‌پال ─────────────────────────────────────────────── */}
      <Card
        loading={loading}
        style={{ marginBottom: 20 }}
        title={
          <Space>
            <DollarOutlined />
            <span>زرین‌پال</span>
            {activeGateway === 'zarinpal' && <Tag color="blue">درگاه فعال پیش‌فرض</Tag>}
          </Space>
        }
        extra={
          <Space>
            <Text type="secondary">{zarinpal.enabled ? 'فعال' : 'غیرفعال'}</Text>
            <Switch
              checked={zarinpal.enabled}
              onChange={(checked) => handleToggleGateway('zarinpal', checked)}
              disabled={saving}
            />
          </Space>
        }
      >
        <Form form={zarinpalForm} layout="vertical" onFinish={handleSaveAll}>
          <Form.Item
            name="merchantId"
            label="شناسه پذیرنده (Merchant ID)"
            help={
              zarinpal.hasMerchantId
                ? 'شناسه ذخیره‌شده وجود دارد. برای تغییر، شناسه جدید وارد کنید. در غیر این‌صورت خالی بگذارید.'
                : 'شناسه پذیرنده را از پنل زرین‌پال دریافت کنید'
            }
          >
            <Input.Password
              placeholder={zarinpal.hasMerchantId ? `${zarinpal.merchantId} (بدون تغییر)` : 'شناسه پذیرنده خود را وارد کنید'}
              style={{ fontFamily: 'monospace', direction: 'ltr' }}
            />
          </Form.Item>

          <Form.Item label="حالت آزمایشی (Sandbox)" help="در حالت آزمایشی، پرداخت‌ها واقعی نیستند — فقط برای تست">
            <Switch checked={zarinpal.sandbox} onChange={(v) => setZarinpal((p) => ({ ...p, sandbox: v }))} />
          </Form.Item>

          <Text type="secondary" style={{ fontSize: 12 }}>
            پنل پذیرندگان: <a href="https://next.zarinpal.com" target="_blank" rel="noreferrer">next.zarinpal.com</a>
          </Text>
        </Form>
      </Card>

      {/* ─── زیبال ────────────────────────────────────────────────── */}
      <Card
        loading={loading}
        style={{ marginBottom: 20 }}
        title={
          <Space>
            <DollarOutlined />
            <span>زیبال</span>
            {activeGateway === 'zibal' && <Tag color="blue">درگاه فعال پیش‌فرض</Tag>}
          </Space>
        }
        extra={
          <Space>
            <Text type="secondary">{zibal.enabled ? 'فعال' : 'غیرفعال'}</Text>
            <Switch
              checked={zibal.enabled}
              onChange={(checked) => handleToggleGateway('zibal', checked)}
              disabled={saving}
            />
          </Space>
        }
      >
        <Form form={zibalForm} layout="vertical" onFinish={handleSaveAll}>
          <Form.Item
            name="merchant"
            label="کد پذیرنده (Merchant)"
            help={
              zibal.hasMerchant
                ? 'کد پذیرنده ذخیره‌شده وجود دارد. برای تغییر، کد جدید وارد کنید. در غیر این‌صورت خالی بگذارید.'
                : 'کد پذیرنده را از پنل زیبال دریافت کنید. تا زمانی که «حالت آزمایشی» روشن است، از merchant تستی زیبال استفاده می‌شود.'
            }
          >
            <Input.Password
              placeholder={zibal.hasMerchant ? `${zibal.merchant} (بدون تغییر)` : 'کد پذیرنده خود را وارد کنید'}
              style={{ fontFamily: 'monospace', direction: 'ltr' }}
            />
          </Form.Item>

          <Form.Item label="حالت آزمایشی (Sandbox)" help="در حالت آزمایشی از merchant تستی زیبال استفاده می‌شود و پرداخت‌ها واقعی نیستند">
            <Switch checked={zibal.sandbox} onChange={(v) => setZibal((p) => ({ ...p, sandbox: v }))} />
          </Form.Item>

          <Text type="secondary" style={{ fontSize: 12 }}>
            پنل پذیرندگان: <a href="https://zibal.ir" target="_blank" rel="noreferrer">zibal.ir</a>
          </Text>
        </Form>
      </Card>

      {/* ─── OxaPay (کریپتو) ──────────────────────────────────────── */}
      <Card
        loading={loading}
        style={{ marginBottom: 20 }}
        title={
          <Space>
            <DollarOutlined />
            <span>OxaPay (کریپتو)</span>
            {activeGateway === 'oxapay' && <Tag color="blue">درگاه فعال پیش‌فرض</Tag>}
            <Tag color="gold">سایت بین‌المللی همیشه از این درگاه استفاده می‌کند</Tag>
          </Space>
        }
        extra={
          <Space>
            <Text type="secondary">{oxapay.enabled ? 'فعال' : 'غیرفعال'}</Text>
            <Switch
              checked={oxapay.enabled}
              onChange={(checked) => handleToggleGateway('oxapay', checked)}
              disabled={saving}
            />
          </Space>
        }
      >
        <Form form={oxapayForm} layout="vertical" onFinish={handleSaveAll}>
          <Form.Item
            name="apiKey"
            label="کلید Payment API (merchant_api_key)"
            help={
              oxapay.hasApiKey
                ? 'کلید ذخیره‌شده وجود دارد. برای تغییر، کلید جدید وارد کنید. در غیر این‌صورت خالی بگذارید.'
                : 'کلید «Payment API» را از پنل OxaPay دریافت کنید — همان کلیدی که برای ساخت فاکتور و بررسی امضای وب‌هوک استفاده می‌شود (با کلید General API فرق دارد).'
            }
          >
            <Input.Password
              placeholder={oxapay.hasApiKey ? `${oxapay.apiKey} (بدون تغییر)` : 'کلید Payment API را وارد کنید'}
              style={{ fontFamily: 'monospace', direction: 'ltr' }}
            />
          </Form.Item>

          <Form.Item
            name="generalApiKey"
            label="کلید General API (اختیاری)"
            help={
              oxapay.hasGeneralApiKey
                ? 'کلید ذخیره‌شده وجود دارد. برای تغییر، کلید جدید وارد کنید.'
                : 'اختیاری — فقط برای قابلیت‌های سطح حساب (مثلاً نمایش موجودی) در آینده لازم می‌شود.'
            }
          >
            <Input.Password
              placeholder={oxapay.hasGeneralApiKey ? `${oxapay.generalApiKey} (بدون تغییر)` : 'کلید General API (اختیاری)'}
              style={{ fontFamily: 'monospace', direction: 'ltr' }}
            />
          </Form.Item>

          <Form.Item
            name="usdToTomanRate"
            label="نرخ تبدیل تومان به دلار (چند تومان = ۱ دلار)"
            help="چون فاکتور OxaPay به دلار ساخته می‌شود، قیمت‌های تومانی سایت (بلیت قطار، بسته‌ها و ...) با این نرخ به دلار تبدیل می‌شوند. بدون تنظیم این مقدار، پرداخت با OxaPay شکست می‌خورد."
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder="مثلاً 950000" />
          </Form.Item>

          <Form.Item
            name="donationLink"
            label="لینک حمایت مالی کریپتویی (Payment Link)"
            help="لینک ثابتی که از پنل OxaPay (بخش Payment Link) ساخته‌اید — برای دکمه «حمایت مالی» در نسخه انگلیسی سایت (potterheads.org) استفاده می‌شود."
          >
            <Input placeholder="https://pay.oxapay.com/xxxxxxxx" style={{ direction: 'ltr' }} />
          </Form.Item>

          <Form.Item label="حالت آزمایشی (Sandbox)" help="در حالت آزمایشی، فاکتورها با sandbox: true ساخته می‌شوند و پرداخت‌ها واقعی نیستند">
            <Switch checked={oxapay.sandbox} onChange={(v) => setOxapay((p) => ({ ...p, sandbox: v }))} />
          </Form.Item>

          <Text type="secondary" style={{ fontSize: 12 }}>
            پنل پذیرندگان: <a href="https://app.oxapay.com" target="_blank" rel="noreferrer">app.oxapay.com</a>
            {' · '}مستندات: <a href="https://docs.oxapay.com" target="_blank" rel="noreferrer">docs.oxapay.com</a>
          </Text>
        </Form>
      </Card>

      <Divider />

      <Button
        type="primary"
        icon={<SaveOutlined />}
        loading={saving}
        size="large"
        onClick={handleSaveAll}
      >
        ذخیره همه تنظیمات
      </Button>
    </div>
  );
}
